/*
 * @description: 消息推送接入事件中心
 * demo: https://gitlab.huolala.cn/group-biz-platform/intelligence-center/intelligence-client-h5
 */

import md5 from 'crypto-js/md5';
import { apiHostMqtt } from '@/apis/hosts';
import { log } from '@/utils/log';
import { getToken } from '@/apis/util';

// 实例 wrapper
// @params mqttInstance: mqtt 实例
// @params msgCb: 消息回调
let mqttWrapper = null;
// 重连定时器
let reconnectTimer = null;

// 与服务器建立连接
const connetServer = async function (config, clientId) {
  const { token, host, port } = config;

  const Paho = await import('paho-mqtt');

  // 客户端连接
  const client = new Paho.Client(host, port, '/intelligence', clientId);
  client.onConnected = onConnectSuccess;
  client.onConnectionLost = onConnectionLost;
  client.onMessageDelivered = onMessageDelivered;
  client.onMessageArrived = onMessageArrived;

  // 连接参数
  const connectOptions = {
    // 连接超时时间
    timeout: 30,
    // 连接服务器用户名，默认为clientId
    userName: clientId,
    // 连接服务器密码
    password: token,
    // 客户端保活周期(心跳)
    keepAliveInterval: 10,
    // 连接断开是否清除session
    cleanSession: false,
    // 是否使用SSL安全通信
    useSSL: true,
    // 连接成功回调
    onSuccess,
    // 连接失败回调
    onFailure,
    // 禁用自动重连，改为手动控制
    reconnect: false,
    // 连接服务器使用的Mqtt协议版本
    mqttVersion: 4,
    // 根据指定的版本强制连接
    mqttVersionExplicit: true,
  };

  // 执行连接
  client.connect(connectOptions);
  return client;
};

// 生成sign签名
function generatorSign(param) {
  const keys = [];
  for (const key of param.keys()) {
    keys.push(key);
  }
  keys.sort();
  let str = '';
  for (const key of keys) {
    str += key + param.get(key);
  }
  const sign = md5(str).toString();
  return sign.toUpperCase();
}

// 获取连接配置信息
function getConfigInfo(configUrl, clientId) {
  const ctime = Date.parse(new Date()) / 1000;
  const accessToken = getToken();
  const data = {
    clientId,
    ctime,
    accessToken,
    enableSsl: true,
  };

  const dataJson = encodeURIComponent(JSON.stringify(data));
  const signMap = new Map();
  signMap.set('clientId', clientId);
  signMap.set('ctime', ctime);
  signMap.set('accessToken', accessToken);
  const sign = generatorSign(signMap);
  const url = `${configUrl}?name=center.getConfigInfo&data=${dataJson}&sign=${sign}`;

  let response = null;
  try {
    const xmlHttp = new XMLHttpRequest();
    xmlHttp.open('GET', url, false); // false 为同步请求
    xmlHttp.send();
    response = jsonParse(xmlHttp.responseText);
  } catch (err) {
    log.log('--------------请求事件中心基础信息报错', err);
  }

  return response?.data;
}

// 检查是否连接成功
function isConnected() {
  return mqttWrapper?.mqttInstance?.isConnected() || false;
}

// 当服务器连接失败后回调
function onFailure(invocationContext, errorCode, errorMessage) {
  log.log('事件通道H5客户端连接失败:', invocationContext);
  log.error(
    `事件通道H5客户端连接失败，失败code： ${errorCode} ，失败原因：${errorMessage}`
  );
  
  // 连接失败时也触发重连机制
  handleReconnect();
}

// 当服务器连接成功后回调
function onSuccess(invocationContext) {
  log.log(`验证onSuccess内容： ${JSON.stringify(invocationContext)}`);
  // 连接成功时重置重连计数
  if (mqttWrapper) {
    mqttWrapper.reconnectCount = 0;
  }
  
  // 连接成功时清理重连定时器
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

// 当服务器连接成功后回调
function onConnectSuccess(reconnect, serverURI) {
  log.log(
    `事件通道H5客户端连接成功，是否重连： ${reconnect} ，服务器地址：${serverURI}`
  );
}

// 当断开连接时回调
function onConnectionLost(responseObject) {
  log.log(
    `事件通道H5客户端连接断开，原因信息： ${JSON.stringify(responseObject)}`
  );
  
  // 连接丢失时触发重连机制
  handleReconnect();
}

// 统一的重连处理函数
function handleReconnect() {
  // 检查是否需要重连
  if (mqttWrapper && mqttWrapper.reconnectCount < mqttWrapper.maxReconnectAttempts) {
    mqttWrapper.reconnectCount++;
    log.log(`尝试第 ${mqttWrapper.reconnectCount} 次重连`);
    
    // 清理之前的重连定时器
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    
    // 延迟重连，避免过于频繁
    reconnectTimer = setTimeout(() => {
      // 刷新配置并重新连接
      refreshTokenAndReconnect();
      reconnectTimer = null;
    }, 2000 * mqttWrapper.reconnectCount); // 逐步增加延迟时间
  } else {
    log.error('达到最大重连次数，停止重连');
  }
}

// 发布消息回调
function onMessageDelivered(message) {
  log.log(`发布消息成功，发布内容： ${JSON.stringify(message.payloadString)}`);
}

// JSON.parse
function jsonParse(str) {
  try {
    const result = JSON.parse(str);
    return result;
  } catch (e) {
    return false;
  }
}

// 接受消息回调
function onMessageArrived(message) {
  // 解析消息内容
  const result = jsonParse(message.payloadString) || {};
  if (result.bizContent && result.bizContent.bizContent) {
    result.bizContent.bizContent =
      jsonParse(result.bizContent.bizContent) || {};
  }
  log.log('event center message:', result);
  if (typeof mqttWrapper.msgCb === 'function') {
    mqttWrapper.msgCb(result.bizContent || {});
  } else {
    log.error('msgCb 必须是一个函数');
  }
}

// 订阅成功以及订阅失败回调
function onSubscribeSuccess() {
  log.log('事件通道H5客户端订阅Push&Im消息成功！！');
}
function onSubscribeFailure(errorCode, errorMessage) {
  log.log(
    `事件通道H5客户端订阅Push&Im消息失败，失败code：${errorCode} ，失败原因：${errorMessage}`
  );
}

// 订阅成功以及订阅失败回调
function onUnSubscribeSuccess() {
  log.log('事件通道H5客户端取消订阅Push&Im消息成功！！');
}
function onUnSubscribeFailure(errorCode, errorMessage) {
  log.log(
    `事件通道H5客户端取消订阅Push&Im消息失败，失败code：${errorCode}，失败原因：${errorMessage},`
  );
}

// 提取公共连接逻辑
async function establishConnection(config, clientId) {
  // 如果已有连接且已连接，先断开
  if (mqttWrapper?.mqttInstance?.isConnected()) {
    mqttWrapper.mqttInstance.disconnect();
  }
  
  // 建立新连接
  return await connetServer(config, clientId);
}

// 刷新token并重新连接
async function refreshTokenAndReconnect() {
  if (!mqttWrapper) return;
  
  try {
    // 检查用户是否仍然登录
    const currentUserToken = getToken();
    if (!currentUserToken) {
      log.error('用户已退出登录，停止重连');
      return;
    }
    
    // 重新获取配置信息
    const config = getConfigInfo(apiHostMqtt, mqttWrapper.clientId);
    if (!config) {
      log.error('无法获取新的配置信息，稍后重试');
      // 如果获取配置失败，触发重连机制
      handleReconnect();
      return;
    }
    
    // 使用新获取的配置信息（包含新的token）重新连接
    mqttWrapper.mqttInstance = await establishConnection(config, mqttWrapper.clientId);
  } catch (error) {
    log.error('刷新token并重连失败:', error);
  }
}

// 给外部调用 -- 创建连接
async function createWebSocket(options = {}) {
  const {
    userId,
    deviceType = 'h5_cn_eweb',
    deviceId = '99999999',
    msgCb,
  } = options;

  // 唯一标识，必须
  if (!userId) {
    log.error('userId 是必填的，获取到的 userId 类型', typeof userId);
    return;
  }
  
  // 只是校验用户是否已经有登陆
  const accessToken = getToken();
  if (!accessToken) {
    log.error('token 是必填的，获取到的 token 类型', typeof accessToken);
    return;
  }

  // 拼接 clientId
  const clientId = `/${deviceType}/${userId}/${deviceId}`;
  console.log('>>> ~ createWebSocket ~ clientId:', clientId);

  // 获取连接配置
  const config = getConfigInfo(apiHostMqtt, clientId);

  if (!config) {
    log.error('获取配置信息失败，触发重连机制');
    // 如果获取配置失败，初始化重连计数并触发重连机制
    if (mqttWrapper) {
      mqttWrapper.reconnectCount = 0;
    }
    handleReconnect();
    return;
  }
  
  log.log('mqtt--createWebSocket', options, config, mqttWrapper);

  // 创建链接
  if (!mqttWrapper?.mqttInstance) {
    mqttWrapper = {
      msgCb, // 收到消息后的回调
      mqttInstance: await establishConnection(config, clientId), // 连接实例
      reconnectCount: 0, // 重连次数
      maxReconnectAttempts: 10, // 最大重连次数
      userId,
      deviceType,
      deviceId,
      clientId
    };
  } else {
    // 已创建，更新回调和用户信息
    mqttWrapper.msgCb = msgCb;
    mqttWrapper.userId = userId;
    mqttWrapper.deviceType = deviceType;
    mqttWrapper.deviceId = deviceId;
    mqttWrapper.clientId = clientId;
    // 重置重连计数
    mqttWrapper.reconnectCount = 0;
    // 重新连接
    mqttWrapper.mqttInstance = await establishConnection(config, clientId);
  }
}

// 给外部调用 -- 断开连接
function disconnectWebsocket() {
  log.log('mqtt--disconnectWebsocket');
  if (mqttWrapper?.mqttInstance) {
    const tmpIsConnected = mqttWrapper?.mqttInstance.isConnected();
    if (tmpIsConnected) {
      // 断开连接
      mqttWrapper.mqttInstance.disconnect();
    }
    // 重置重连计数
    mqttWrapper.reconnectCount = 0;
    mqttWrapper = null;
  }
  
  // 清理重连定时器
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

// 给外部调用 -- 订阅消息
function subscribe(subTopic) {
  const subscribeOptions = {};
  subscribeOptions.qos = 2; // 默认都订阅2
  subscribeOptions.onSuccess = onSubscribeSuccess;
  subscribeOptions.onFailure = onSubscribeFailure;
  mqttWrapper.mqttInstance.subscribe(subTopic, subscribeOptions);
}

// 给外部调用 -- 取消订阅
function unSubscribe(subTopic) {
  const unsubscribeOptions = {};
  unsubscribeOptions.onSuccess = onUnSubscribeSuccess;
  unsubscribeOptions.onFailure = onUnSubscribeFailure;
  mqttWrapper.mqttInstance.unsubscribe(subTopic);
}

// 目前服务器没限制订阅操作，只要连接成功即可正常收发消息。（订阅待服务器完善后再同步，待联调）
export default {
  subscribe,
  unSubscribe,
  createWebSocket,
  disconnectWebsocket,
  isConnected,
};